import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { LogIn } from 'lucide-react'
import { Loader2 } from 'lucide-react'
import { TriangleAlert } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ChoiceButton, ConfirmButton, SectionLabel } from '@/components/bookings/dialogKit'
import {
  checkInBooking,
  getPaymentOptions,
  type PaymentOptions,
  type SettlePackageOption,
} from '@/lib/api'
import { readApiError } from '@/lib/apiError'
import type { CalendarEvent } from '@/lib/render'
import { formatTime } from '@/components/render/renderers/calendar/shared'
import { creditIneligibleWhy, creditCostSuffix } from '@/lib/creditReasons'

// ─── "Check in" ────────────────────────────────────────────────────────────────
//
// The customer has arrived. Requested 2026-08-17: "Mark the client as arrived and
// deduct the appropriate credit. Please show which package credit will be used
// before confirming, then update the balance automatically."
//
// SHOWING WHICH CREDIT IS THE WHOLE POINT of this dialog existing rather than a
// one-tap menu item. `GET /:id/payment-options` is a pure read — it answers "how
// could this be paid" without spending anything — so the operator sees the named
// package before any session is burned. The tap then sends that package's id
// explicitly, and `/check-in` honours or refuses an explicit id, never
// substitutes another.
//
// ARRIVAL IS STILL RECORDED WHEN NO CREDIT APPLIES. Somebody walked in; that is
// true regardless of how the session gets paid for. The dialog says plainly that
// nothing will be deducted, so the operator knows to take payment — silence there
// is how a session gets paid for twice, or not at all.

export interface CheckInDialogProps {
  event: CalendarEvent | null
  onOpenChange: (open: boolean) => void
  onChanged?: () => void
}

function creditLine(option: SettlePackageOption): string {
  // Booking-scoped: on a per-service package these are the booked SERVICE's own
  // counts, so the label says which allowance the numbers describe.
  const left = `${option.sessions_remaining} of ${option.sessions_total}${option.per_service ? ' for this service' : ''} left`
  return `${left}${creditCostSuffix(option)}`
}

export function CheckInDialog({ event, onOpenChange, onChanged }: CheckInDialogProps) {
  const open = event !== null

  const [options, setOptions] = useState<PaymentOptions | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [chosenPackageId, setChosenPackageId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !event) return
    let live = true
    setOptions(null)
    setChosenPackageId(null)
    setLoadError(null)
    setError(null)
    setBusy(false)
    setLoading(true)
    getPaymentOptions(event.id)
      .then((res) => {
        if (!live) return
        setOptions(res)
        // The SERVER decides whether the choice is forced enough to preselect —
        // `auto_spendable_customer_package_id` is resolveCredit's answer, and it
        // is null when several packages qualify. Re-deriving that here would mean
        // two places deciding whether spending a $999 credit on a $40 session is
        // automatic, and them disagreeing is how the wrong one gets emptied.
        setChosenPackageId(res.package_credit.auto_spendable_customer_package_id)
      })
      .catch((e) => { if (live) setLoadError(readApiError(e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [open, event?.id])

  const eligible = options?.package_credit.eligible ?? []
  // The server already trims this to packages worth knowing about: live ones,
  // and expired ones with credits left over. Everything spent-and-gone is only
  // a count (ineligible_omitted) — listing eleven dead packages was burying the
  // one credit the operator could act on.
  const ineligible = options?.package_credit.ineligible ?? []
  const omitted = options?.package_credit.ineligible_omitted ?? 0
  // They hold SOMETHING, usable here or not — distinct from holding nothing at
  // all, and no longer derivable from ineligible.length now that dead packages
  // are withheld from the list.
  const holdsPackages = options?.package_credit.unavailable_reason !== 'no_packages'
  // `payable: false` covers every "nothing to charge" case the endpoint knows
  // about — already paid in cash, already debited, a zero-price session.
  const alreadyPaid = options !== null && !options.payable

  async function confirm() {
    if (!event || busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await checkInBooking(event.id, chosenPackageId ?? undefined)
      // Say what actually happened to the credit, never what was hoped for. A
      // "checked in" toast that stays silent about an unspent credit is how an
      // operator later takes cash for a session a package already covered.
      const description = result.package_debited
        ? `${result.package_name} · ${result.sessions_remaining} of ${result.sessions_total} left`
        : result.covered_by_package
          ? 'Already paid by a package credit.'
          : 'No credit was deducted — take payment for this session.'
      toast.success(`${event.customer_name ?? 'Customer'} checked in`, { description })
      onChanged?.()
      onOpenChange(false)
    } catch (e) {
      setError(readApiError(e))
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onOpenChange(false) }}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Check in</DialogTitle>
          <DialogDescription>
            {event
              ? `${event.customer_name ?? 'Customer'} · ${event.service_name} · ${formatTime(event.start)}`
              : ''}
          </DialogDescription>
        </DialogHeader>

        {event ? (
          <div className="flex flex-col gap-4">
            {/* The clear warning before check-in the operator asked for: an
                intake answer flagged medical clearance and nobody has granted
                it. A WARNING, not a block — the operator in the room knows
                things the system cannot, and arrival is still a fact. */}
            {event.clearance_pending ? (
              <div className="flex items-start gap-2 rounded-lg border border-amber-500/60 bg-amber-500/10 px-3 py-2.5">
                <TriangleAlert className="mt-px size-4 shrink-0 text-amber-600" aria-hidden />
                <p className="text-[12px] text-foreground">
                  <span className="font-semibold">Medical clearance required — not yet granted.</span>{' '}
                  Their intake answers flagged this. Review them (Intake form on this booking)
                  before going ahead.
                </p>
              </div>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <SectionLabel>Credit to use</SectionLabel>

              {loading ? (
                <div className="flex items-center gap-2 py-2 text-[12.5px] text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  Checking what they hold…
                </div>
              ) : loadError ? (
                <p className="text-[12.5px] text-destructive">{loadError}</p>
              ) : alreadyPaid ? (
                <p className="rounded-lg border border-border bg-muted px-3 py-2 text-[12.5px] text-muted-foreground">
                  This session is already paid for, so nothing will be deducted.
                </p>
              ) : eligible.length === 0 ? (
                <div className="flex items-start gap-2 rounded-lg border border-amber-500/50 bg-amber-500/10 px-3 py-2">
                  <TriangleAlert className="mt-px size-3.5 shrink-0 text-amber-600" aria-hidden />
                  <p className="text-[11.5px] text-foreground">
                    {holdsPackages
                      ? 'None of their packages can cover this session, so no credit will be deducted — take payment for it.'
                      : 'They hold no package credits, so no credit will be deducted — take payment for this session.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-1.5">
                    {eligible.map((p) => (
                      <ChoiceButton
                        key={p.customer_package_id}
                        title={p.name}
                        subtitle={creditLine(p)}
                        selected={chosenPackageId === p.customer_package_id}
                        onClick={() => setChosenPackageId(p.customer_package_id)}
                      />
                    ))}
                  </div>
                  {eligible.length > 1 && chosenPackageId === null ? (
                    <p className="text-[11.5px] text-muted-foreground">
                      They have more than one usable credit — pick which to spend.
                    </p>
                  ) : null}
                </>
              )}

              {/* Why the others are out. An operator asking "she has a package,
                  why is it not offered?" gets an answer here instead of guessing.
                  Only packages that could still pay for something appear;
                  spent-and-expired ones are a single count so the list is
                  explicitly short, never silently so. */}
              {(ineligible.length > 0 || omitted > 0) && !alreadyPaid ? (
                <div className="mt-1 flex flex-col gap-0.5">
                  {ineligible.map((p) => (
                    <span key={p.customer_package_id} className="text-[11px] text-muted-foreground">
                      {p.name} — {creditIneligibleWhy(p)}
                      {p.ineligible_reason === 'expired' && (p.package_sessions_remaining ?? 0) > 0
                        ? ` (${p.package_sessions_remaining} unused)`
                        : ''}
                    </span>
                  ))}
                  {omitted > 0 ? (
                    <span className="text-[11px] text-muted-foreground/70">
                      {omitted} spent or expired {omitted === 1 ? 'package' : 'packages'} not shown
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>

            {error ? <p className="text-[12.5px] text-destructive">{error}</p> : null}

            <div className="flex flex-col gap-2 border-t border-border pt-3">
              <ConfirmButton
                // Deliberately never just "Check in": that is the label on the
                // sheet row that opened this dialog, and two identical buttons on
                // screen is one the operator presses by accident.
                label={
                  chosenPackageId
                    ? 'Check in and deduct the credit'
                    : 'Check in without a credit'
                }
                icon={<LogIn className="size-4" aria-hidden />}
                busy={busy}
                // Never blocked on the credit question: arrival is a fact, and
                // refusing to record it because the money is unclear is the wrong
                // way round.
                disabled={loading}
                onClick={() => void confirm()}
              />
              <p className="text-center text-[11px] text-muted-foreground">
                {chosenPackageId
                  ? 'The balance updates automatically.'
                  : 'Marks them arrived. The session stays unpaid.'}
              </p>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
